import { useState, useEffect, useCallback } from "react";
import { Mail, Phone, MapPin, Calendar, MessageSquare, Eye, LogOut, Check, RotateCcw } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hb_admin_token";

function formatDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return iso; }
}

export default function Admin() {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [anfragen, setAnfragen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const isAuth = Boolean(token);
  const unreadCount = anfragen.filter(a => !a.gelesen).length;

  const loadAnfragen = useCallback(async (activeToken) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/kontakt`, {
        headers: { Authorization: `Bearer ${activeToken}` }
      });
      const sorted = [...(res.data || [])].sort((a, b) =>
        new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
      );
      setAnfragen(sorted);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Sitzung abgelaufen. Bitte erneut anmelden.");
      } else {
        setError("Fehler beim Laden der Anfragen.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadAnfragen(token);
  }, [token, loadAnfragen]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const newToken = res.data.token;
      sessionStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setPassword("");
    } catch {
      setError("Falsches Passwort.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* ignore */ }
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAnfragen([]);
    setSelected(null);
  };

  const toggleGelesen = async (anfrage) => {
    const id = anfrage.id;
    const endpoint = anfrage.gelesen ? "ungelesen" : "gelesen";
    setMarkingId(id);
    try {
      await axios.put(`${API}/kontakt/${id}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnfragen(prev => prev.map(a =>
        a.id === id
          ? { ...a, gelesen: !a.gelesen, gelesen_am: !a.gelesen ? new Date().toISOString() : null }
          : a
      ));
      if (selected?.id === id) {
        setSelected(prev => ({ ...prev, gelesen: !prev.gelesen }));
      }
    } catch {
      setError("Status konnte nicht geändert werden.");
    } finally {
      setMarkingId(null);
    }
  };

  if (!isAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hb-off-white pt-20">
        <div className="max-w-sm w-full mx-auto px-6">
          <div className="bg-white p-10 border border-hb-border">
            <h1 className="text-xl font-manrope font-bold text-hb-dark mb-8 text-center">
              Admin – Kontaktanfragen
            </h1>
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                  Passwort
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="admin-password"
                  className="input-hb"
                  autoFocus
                />
              </div>
              {error && <p className="text-sm text-red-500" data-testid="admin-error">{error}</p>}
              <button
                type="submit"
                data-testid="admin-login-btn"
                disabled={loginLoading}
                className="btn-primary w-full text-center"
              >
                {loginLoading ? "..." : "Anmelden"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 md:pt-24 min-h-screen bg-hb-off-white">
      <div className="bg-white border-b border-hb-border">
        <div className="container-hb py-6 flex items-center justify-between">
          <div>
            <span className="label-text text-hb-wood block mb-1">Admin</span>
            <h1 className="text-xl font-manrope font-bold text-hb-dark flex items-center gap-3">
              Kontaktanfragen
              <span className="text-sm font-normal text-hb-muted">
                ({anfragen.length} gesamt)
              </span>
              {unreadCount > 0 && (
                <span
                  data-testid="unread-badge"
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-hb-dark"
                  style={{ background: "#f5c800" }}
                >
                  {unreadCount}
                </span>
              )}
            </h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => loadAnfragen(token)} className="btn-secondary text-xs">
              Aktualisieren
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="btn-secondary text-xs flex items-center gap-1"
            >
              <LogOut size={11} /> Abmelden
            </button>
          </div>
        </div>
      </div>

      <div className="container-hb py-8">
        {loading && <p className="text-hb-muted text-center py-20">Wird geladen...</p>}
        {error && <p className="text-sm text-red-500 text-center py-4">{error}</p>}

        {!loading && anfragen.length === 0 && !error && (
          <p className="text-hb-muted text-center py-20">Noch keine Anfragen vorhanden.</p>
        )}

        {!loading && anfragen.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* List */}
            <div className="lg:col-span-1 space-y-2">
              {anfragen.map((a, i) => (
                <button
                  key={a.id || i}
                  onClick={() => setSelected(a)}
                  data-testid={`anfrage-item-${i}`}
                  className={`w-full text-left p-4 border transition-all relative ${
                    selected?.id === a.id
                      ? 'border-hb-dark bg-white'
                      : 'border-hb-border bg-white hover:border-hb-dark'
                  }`}
                >
                  {!a.gelesen && (
                    <span className="absolute top-3 right-3 w-2 h-2 rounded-full" style={{ background: "#f5c800" }} />
                  )}
                  <div className="flex items-center justify-between mb-1 pr-4">
                    <span className={`font-manrope font-bold text-sm ${a.gelesen ? 'text-hb-muted' : 'text-hb-dark'}`}>
                      {a.name || "–"}
                    </span>
                    <span className="text-[10px] text-hb-muted font-inter">{formatDate(a.timestamp).split(",")[0]}</span>
                  </div>
                  <p className="text-xs text-hb-wood font-semibold">{a.projektart || "–"}</p>
                  <p className="text-xs text-hb-muted mt-0.5 truncate">{a.nachricht?.slice(0, 60) || "–"}</p>
                </button>
              ))}
            </div>

            {/* Detail */}
            <div className="lg:col-span-2">
              {!selected ? (
                <div className="bg-white border border-hb-border p-10 text-center text-hb-muted">
                  <Eye size={24} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-inter">Anfrage auswählen</p>
                </div>
              ) : (
                <div className="bg-white border border-hb-border p-8 space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-manrope font-bold text-hb-dark">{selected.name}</h2>
                      <p className="text-xs text-hb-muted font-inter mt-1">{formatDate(selected.timestamp)}</p>
                    </div>
                    <span className="text-[10px] tracking-widest uppercase text-hb-wood font-semibold border border-hb-wood/30 px-2 py-1">
                      {selected.projektart || "–"}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: <Mail size={13} />, label: "E-Mail", val: selected.email, href: `mailto:${selected.email}` },
                      { icon: <Phone size={13} />, label: "Telefon", val: selected.telefon || "–", href: selected.telefon ? `tel:${selected.telefon}` : null },
                      { icon: <MapPin size={13} />, label: "Ort", val: selected.ort || "–" },
                      { icon: <Calendar size={13} />, label: "Eingegangen", val: formatDate(selected.timestamp) },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-hb-wood mt-0.5">{item.icon}</span>
                        <div>
                          <p className="text-[10px] tracking-widest uppercase text-hb-muted font-semibold">{item.label}</p>
                          {item.href ? (
                            <a href={item.href} className="text-sm text-hb-dark hover:text-hb-wood transition-colors font-inter">
                              {item.val}
                            </a>
                          ) : (
                            <p className="text-sm text-hb-dark font-inter">{item.val}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <p className="text-[10px] tracking-widest uppercase text-hb-muted font-semibold mb-2 flex items-center gap-1">
                      <MessageSquare size={11} /> Nachricht
                    </p>
                    <div className="bg-hb-off-white p-4 border-l-2 border-hb-wood">
                      <p className="text-sm text-hb-text font-inter leading-relaxed whitespace-pre-wrap">
                        {selected.nachricht || "–"}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 pt-2 border-t border-hb-border">
                    {selected.email && (
                      <a
                        href={`mailto:${selected.email}?subject=Re: Holzbuddy Anfrage – ${selected.projektart}`}
                        className="btn-primary text-xs"
                        data-testid="reply-btn"
                      >
                        Antworten
                      </a>
                    )}
                    {selected.telefon && (
                      <a href={`tel:${selected.telefon}`} className="btn-secondary text-xs flex items-center gap-1">
                        <Phone size={11} /> Anrufen
                      </a>
                    )}
                    <button
                      onClick={() => toggleGelesen(selected)}
                      disabled={markingId === selected.id}
                      data-testid="mark-gelesen-btn"
                      className="btn-secondary text-xs flex items-center gap-1 ml-auto"
                    >
                      {selected.gelesen
                        ? <><RotateCcw size={11} /> Als ungelesen markieren</>
                        : <><Check size={11} /> Als gelesen markieren</>
                      }
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
